// UpNextCard — H1-Phase-1-Track-2-Sub-2.1.
//
// 3:4-Cover-Card für die UpNext-Rail nach Colophon. Source-of-truth:
// docs/mockups/viewer.jsx:671-718 `function UpNextCard`. Distinct von
// feed/ScrollyCard wegen Index-Overlay (№01-Nummerierung Instrument
// Serif italic), Category-Bottom-Left-Overlay, Cover-direkt-Gradient
// statt Inner-Cover-Component, plus deutlich kompaktere Meta-Row.
//
// Variant-Map: heute nur "bold" implementiert (matches aktueller
// Colophon-Hardcoded-Dark-Background). "subtle" ist Plan-2-Backlog.
//
// Plan-2-Backlog-Items (siehe BACKLOG.md):
//  - "UpNextCard-Type Backend-Recommendation-Endpoint-Shape antizipieren":
//    heute MockScrolly als Props-Type. Bei Backend-Endpoint-Bau muss
//    die Card-API auf einen schmaleren Type (Cover-Palette, Title,
//    Creator-Name, Views, CategoryId) verfeinert werden.
//  - "HorizontalCardRail shared Component": wenn dieses Card-Pattern
//    in weiteren Surfaces auftaucht (Feed-Discovery, Creator-Profile
//    Recent-Tab), kann es zu shared lib/ui/-Component ausgelagert
//    werden. Heute Single-Use, daher inline-implementiert.

import type { MockScrolly } from "../../lib/mock/scrollys";
import { COVER_PALETTES } from "../../lib/tokens";
import { CATEGORY_BY_ID } from "../../lib/categories";
import { formatViews } from "../../lib/format";

export type UpNextCardVariant = "bold" | "subtle";

export type UpNextCardProps = {
  scrolly: MockScrolly;
  index: number;
  variant?: UpNextCardVariant;
};

type Tokens = {
  fg: string;
  muted: string;
  rule: string;
};

const VARIANT_TOKENS: Record<UpNextCardVariant, Tokens> = {
  bold: {
    fg: "#fff",
    muted: "rgba(255,255,255,0.55)",
    rule: "rgba(255,255,255,0.14)",
  },
  // Plan-2-Backlog: subtle-Variant aktuell nicht aktiviert, aber Werte
  // hier reserviert für künftige Migration (matches Mockup).
  subtle: {
    fg: "#1a1a1a",
    muted: "rgba(26,26,26,0.55)",
    rule: "rgba(26,26,26,0.12)",
  },
};

export function UpNextCard({
  scrolly,
  index,
  variant = "bold",
}: UpNextCardProps) {
  const tokens = VARIANT_TOKENS[variant];
  const isBold = variant === "bold";
  const palette = COVER_PALETTES[scrolly.cover.palette];
  const categoryLabel =
    CATEGORY_BY_ID[scrolly.categoryId]?.label ?? scrolly.categoryId;
  const indexLabel = `№${String(index + 1).padStart(2, "0")}`;

  return (
    <a
      href={`/s/${scrolly.slug}`}
      style={{
        textDecoration: "none",
        color: tokens.fg,
        display: "block",
      }}
    >
      <div
        style={{
          aspectRatio: "3/4",
          borderRadius: 10,
          overflow: "hidden",
          background: palette?.bg ?? "#111",
          position: "relative",
          border: isBold ? `1px solid ${tokens.rule}` : "none",
        }}
      >
        {/* Index-Overlay top-left, Instrument Serif italic */}
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontSize: 14,
            fontStyle: "italic",
            color: "#fff",
            textShadow: "0 1px 4px rgba(0,0,0,0.4)",
            opacity: 0.9,
          }}
        >
          {indexLabel}
        </div>
        {/* Category bottom-left, uppercase */}
        <div
          style={{
            position: "absolute",
            bottom: 10,
            left: 10,
            right: 10,
            fontSize: 10.5,
            fontWeight: 600,
            color: "#fff",
            textTransform: "uppercase",
            letterSpacing: 0.8,
            textShadow: "0 1px 4px rgba(0,0,0,0.5)",
          }}
        >
          {categoryLabel}
        </div>
      </div>
      {/* Title — line-clamp 2 */}
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 600,
          marginTop: 12,
          lineHeight: 1.3,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {scrolly.title}
      </div>
      {/* Meta-Row: creator · views */}
      <div
        style={{
          fontSize: 11.5,
          color: tokens.muted,
          marginTop: 5,
          display: "flex",
          gap: 6,
        }}
      >
        <span>{scrolly.creator.name}</span>
        <span>·</span>
        <span>{formatViews(scrolly.stats.views)}</span>
      </div>
    </a>
  );
}
